<template>
    <form class="ActionsFiltersPopover" @submit.prevent="applyFilters">
        <div class="ActionsFiltersPopover__headerBar">
            <div class="ActionsFiltersPopover__headerText">
                Advanced Filters
            </div>
            <NewButton
                class="ActionsFiltersPopover__clearButton"
                variant="flat"
                color="neutral"
                @click="clearFilters"
            >
                CLEAR
            </NewButton>
        </div>
        <div class="ActionsFiltersPopover__filterSection">
            <FormElement class="ActionsFiltersPopover__search" label="Contains">
                <FormInput>
                    <input
                        v-model="localFilters.search"
                        class="ActionsFiltersPopover__searchInput"
                        type="text"
                        placeholder="Value or entity name"
                    >
                </FormInput>
            </FormElement>

            <FormElement class="ActionsFiltersPopover__actionTypes" label="Action Type">
                <FormInput>
                    <Dropdown
                        multi-select
                        :options="actionTypeOptions"
                        :selected="selectedActionTypeOptions"
                        @input="onActionTypesChange"
                    />
                </FormInput>
            </FormElement>

            <FormElement class="ActionsFiltersPopover__scope" label="Type">
                <FormInput>
                    <Dropdown
                        :options="scopeOptions"
                        :selected="selectedScopeOptions"
                        @input="onScopeChange"
                    />
                </FormInput>
            </FormElement>

            <FormElement class="ActionsFiltersPopover__createdDate" label="Date Created">
                <FormInput class="ActionsFiltersPopover__createdFrom">
                    <DatePicker
                        :value="localFilters.createdFrom"
                        placeholder="From"
                        enable-clear
                        @input="onCreatedFromChange"
                    />
                </FormInput>
                <div class="ActionsFiltersPopover__dateSeparator" />
                <FormInput class="ActionsFiltersPopover__createdTo">
                    <DatePicker
                        :value="localFilters.createdTo"
                        :min-date="localFilters.createdFrom"
                        placeholder="To"
                        enable-clear
                        @input="onCreatedToChange"
                    />
                </FormInput>
            </FormElement>

            <template v-if="isCompletedTab">
                <FormElement class="ActionsFiltersPopover__outcome" label="Outcome">
                    <FormInput>
                        <Dropdown
                            :options="outcomeOptions"
                            :selected="selectedOutcomeOptions"
                            @input="onOutcomeChange"
                        />
                    </FormInput>
                </FormElement>

                <FormElement class="ActionsFiltersPopover__resolvedBy" label="Resolved By">
                    <FormInput>
                        <input
                            v-model="localFilters.resolvedBy"
                            class="ActionsFiltersPopover__resolvedByInput"
                            type="text"
                            placeholder="Type an email"
                        >
                    </FormInput>
                </FormElement>

                <FormElement class="ActionsFiltersPopover__resolvedDate" label="Resolved Date">
                    <FormInput class="ActionsFiltersPopover__resolvedFrom">
                        <DatePicker
                            :value="localFilters.resolvedFrom"
                            placeholder="From"
                            enable-clear
                            @input="onResolvedFromChange"
                        />
                    </FormInput>
                    <div class="ActionsFiltersPopover__dateSeparator" />
                    <FormInput class="ActionsFiltersPopover__resolvedTo">
                        <DatePicker
                            :value="localFilters.resolvedTo"
                            :min-date="localFilters.resolvedFrom"
                            placeholder="To"
                            enable-clear
                            @input="onResolvedToChange"
                        />
                    </FormInput>
                </FormElement>
            </template>
        </div>
        <div class="ActionsFiltersPopover__footerBar">
            <NewButton
                class="ActionsFiltersPopover__cancelButton"
                variant="flat"
                color="neutral"
                @click="$emit('close')"
            >
                Cancel
            </NewButton>
            <NewButton
                class="ActionsFiltersPopover__applyButton"
                color="primary"
                type="submit"
            >
                Apply
            </NewButton>
        </div>
    </form>
</template>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import omit from 'lodash/omit';
    import pickBy from 'lodash/pickBy';
    import moment from 'moment';

    import { DatePicker } from '@evidentid/dashboard-commons/components/DatePicker';
    import Dropdown from '@evidentid/dashboard-commons/components/Dropdown/Dropdown.vue';
    import { DropdownOption } from '@evidentid/dashboard-commons/components/Dropdown/types';
    import { FormElement, FormInput } from '@evidentid/dashboard-commons/components/Form';
    import { ActionsTab, ActionType } from '@evidentid/tprm-portal-lib/models/entity-actions-review';

    import NewButton from '@/components/new-button/NewButton.vue';
    import { COMPLETED_ONLY_FILTER_KEYS } from '@/modules/entity-actions-review/constants';
    import { ActionsListFilters } from '@/modules/entity-actions-review/types';

    const actionTypeOptions: DropdownOption[] = [
        { label: 'Entity Name Matching', value: ActionType.entityNameMatchingV1 },
        { label: 'Endorsement Review', value: ActionType.endorsementFormNumberV1 },
        { label: 'Form Number Matching', value: ActionType.complianceWithCategoryResolution },
        { label: 'Collateral Mapping', value: ActionType.evaluatedEntityMappingV1 },
        { label: 'Non-Extraction Review', value: ActionType.nonExtractionCriterionResolutionV1 },
        { label: 'AIE Manuscript Categorization', value: ActionType.aieManuscriptCategorization },
        { label: 'Document Data Review', value: ActionType.documentDataReview },
        { label: 'Contract Extraction', value: ActionType.contractExtraction },
    ];

    const scopeOptions: DropdownOption[] = [
        { label: 'Entity', value: 'entity' },
        { label: 'Global', value: 'global' },
    ];

    const outcomeOptions: DropdownOption[] = [
        { label: 'Accepted', value: 'accepted' },
        { label: 'Rejected', value: 'rejected' },
    ];

    // The DatePicker edits a bare calendar date. Converting a stored instant down to that day's
    // date, in the browser's own timezone, is what lets a previously-applied filter round-trip
    // back into the picker showing the same day the admin originally chose.
    function calendarDateFromInstant(value?: string): string | undefined {
        return value ? moment(value).format('YYYY-MM-DD') : undefined;
    }

    // createdFrom/resolvedFrom mean "at or after this instant" on the wire, so the day the admin
    // picked should be sent as that day's first moment, in the browser's own timezone.
    function startOfDayInstant(date?: string): string | undefined {
        return date ? moment(date).startOf('day').toISOString() : undefined;
    }

    // createdTo/resolvedTo mean "at or before this instant". Sending midnight for the picked day
    // would exclude everything that happened on it, so the day's last moment is sent instead.
    function endOfDayInstant(date?: string): string | undefined {
        return date ? moment(date).endOf('day').toISOString() : undefined;
    }

    export default defineComponent({
        name: 'ActionsFiltersPopover',
        components: {
            DatePicker, Dropdown, FormElement, FormInput, NewButton,
        },
        props: {
            filters: {
                type: Object as PropType<ActionsListFilters>,
                default: (): ActionsListFilters => ({}),
            },
            tab: {
                type: String as PropType<ActionsTab>,
                default: ActionsTab.pending,
            },
        },
        emits: [ 'close', 'submit' ],
        data() {
            return {
                localFilters: {} as ActionsListFilters,
                actionTypeOptions,
                scopeOptions,
                outcomeOptions,
            };
        },
        computed: {
            isCompletedTab(): boolean {
                return this.tab === ActionsTab.completed;
            },
            selectedActionTypeOptions(): DropdownOption[] {
                const selected = this.localFilters.actionTypes || [];
                return this.actionTypeOptions.filter((option) => selected.includes(option.value as string));
            },
            selectedScopeOptions(): DropdownOption[] {
                return this.scopeOptions.filter((option) => option.value === this.localFilters.scope);
            },
            selectedOutcomeOptions(): DropdownOption[] {
                return this.outcomeOptions.filter((option) => option.value === this.localFilters.outcome);
            },
        },
        watch: {
            filters: {
                immediate: true,
                handler(newValue: ActionsListFilters) {
                    this.localFilters = {
                        ...newValue,
                        createdFrom: calendarDateFromInstant(newValue.createdFrom),
                        createdTo: calendarDateFromInstant(newValue.createdTo),
                        resolvedFrom: calendarDateFromInstant(newValue.resolvedFrom),
                        resolvedTo: calendarDateFromInstant(newValue.resolvedTo),
                    };
                },
            },
        },
        methods: {
            // entityId is set by navigating here from an entity, not by this form, so a
            // Clear must not throw it away.
            clearFilters(): void {
                this.localFilters = this.filters.entityId ? { entityId: this.filters.entityId } : {};
            },
            onActionTypesChange(options: DropdownOption[]): void {
                const values = options.map((option) => option.value as string);
                this.localFilters = { ...this.localFilters, actionTypes: values.length > 0 ? values : undefined };
            },
            onScopeChange(option: DropdownOption): void {
                this.localFilters = { ...this.localFilters, scope: option?.value as 'entity' | 'global' };
            },
            onOutcomeChange(option: DropdownOption): void {
                this.localFilters = { ...this.localFilters, outcome: option?.value as 'accepted' | 'rejected' };
            },
            onCreatedFromChange(value: string): void {
                this.localFilters = { ...this.localFilters, createdFrom: value };
            },
            onCreatedToChange(value: string): void {
                this.localFilters = { ...this.localFilters, createdTo: value };
            },
            onResolvedFromChange(value: string): void {
                this.localFilters = { ...this.localFilters, resolvedFrom: value };
            },
            onResolvedToChange(value: string): void {
                this.localFilters = { ...this.localFilters, resolvedTo: value };
            },
            applyFilters(): void {
                const filters: ActionsListFilters = {
                    ...this.localFilters,
                    createdFrom: startOfDayInstant(this.localFilters.createdFrom),
                    createdTo: endOfDayInstant(this.localFilters.createdTo),
                    resolvedFrom: startOfDayInstant(this.localFilters.resolvedFrom),
                    resolvedTo: endOfDayInstant(this.localFilters.resolvedTo),
                };
                // localFilters can still carry Completed-only values gathered while this
                // component was on the Completed tab -- nothing here clears them on a tab
                // change, so submitting on the Pending tab must strip them itself rather than
                // rely on the parent always remounting this component between tabs.
                const scoped = this.isCompletedTab ? filters : omit(filters, COMPLETED_ONLY_FILTER_KEYS);
                this.$emit('submit', pickBy(scoped, (value) => (
                    Array.isArray(value) ? value.length > 0 : Boolean(value)
                )));
            },
        },
    });
</script>
